/**
 * Auth Controller for /try Client.
 *
 * WHAT THIS FILE DOES:
 * - Owns Firebase auth lifecycle (anonymous by default).
 * - Syncs auth state with the extension via the web auth bridge.
 * - Produces an AuthSnapshot that downstream UI + API code can consume.
 *
 * WHY THIS EXISTS:
 * - Keeps auth concerns separate from ingest orchestration.
 * - Avoids mixing DOM/UI code with authentication side effects.
 * - Makes the extension ↔ web bridge explicit and testable.
 *
 * CALLED BY:
 * - src/client/try-client.ts (WebTryClient)
 *
 * THIS FILE CALLS:
 * - firebase/auth for sign-in and token handling
 * - web-auth-bridge.ts to import/export extension auth state
 *
 * STATE NOTES:
 * - workerToken is set when the extension exports an auth state.
 * - suppressExtensionImport prevents ping-pong on explicit logout.
 * - autoSignInBlocked defers anonymous sign-in when extension explicitly logs out.
 *
 * ERROR HANDLING:
 * - Throws WebTryError for auth_not_configured and sign-in failures.
 * - Logs and continues on non-critical bridge errors.
 *
 * DESIGN PRINCIPLES:
 * - Prefer extension worker tokens when available.
 * - Anonymous sign-in keeps /try usable without the extension.
 * - Extension logout suppresses auto-import back into the extension.
 *
 * NON-GOALS:
 * - UI updates (handled by try-ui.ts).
 * - Ingest orchestration (handled by try-client.ts).
 *
 * @file src/client/try-auth.ts
 */

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  GoogleAuthProvider,
  type Auth,
  type User
} from 'firebase/auth';
import { createWebAuthBridge, type WebAuthBridge, type WebAuthState } from './web-auth-bridge.js';
import { WebTryError, extractFirebaseAuthErrorCode, type AuthSnapshot } from './try-shared.js';
import { resolveUserEmailWithReload } from '@gist-protocol/core';
import { clearWebWorkerSession, mintWebWorkerSession } from './web-session-cookie.js';

interface AuthorizationHeaderOptions {
  waitForExtensionMs?: number;
  allowAnonymous?: boolean;
}

interface TrySessionResponse {
  user?: {
    uid?: string;
    email?: string | null;
  } | null;
  billing?: {
    entitlementActive?: boolean;
    subscriptionActive?: boolean;
    isAdmin?: boolean;
  } | null;
}

function hasPaidEntitlement(session: TrySessionResponse | null): boolean | null {
  const billing = session?.billing ?? null;
  if (!billing) {
    return null;
  }
  return Boolean(billing.entitlementActive || billing.subscriptionActive || billing.isAdmin);
}

/**
 * Auth controller that blends Firebase auth with extension worker tokens.
 */
export class WebTryAuthController {
  private auth: Auth | null = null;
  private user: User | null = null;
  private webSessionUserKey: string | null = null;
  private paidEntitlement: boolean | null = null;
  private workerToken: string | null = null;
  private bridge: WebAuthBridge;
  private autoSignInBlocked = false;
  private suppressExtensionImport = false;
  private anonymousSignInInFlight = false;
  private extensionAuthStateWaiters = new Set<() => void>();
  private firebaseAuthReady: Promise<void> = Promise.resolve();
  private resolveFirebaseAuthReady: (() => void) | null = null;
  /** True after `getRedirectResult` settles so auth-ready does not race OAuth return. */
  private firebaseRedirectHandled = false;
  private lastImportedUid: string | null = null;
  private lastWebSessionMintUid: string | null = null;
  private lastAuthErrorCode: string | null = null;
  private listeners = new Set<(snapshot: AuthSnapshot) => void>();

  constructor() {
    this.bridge = createWebAuthBridge({
      onAuthState: (state) => {
        void this.handleExtensionAuthState(state);
      }
    });
  }

  /**
   * Initialize Firebase auth and start listening for extension auth events.
   */
  async init(): Promise<void> {
    this.applyLandingBootstrap();
    this.bridge.listen();
    this.notify();

    const config = window.__GIST_TRY_CONFIG__ ?? null;
    if (!config) {
      return;
    }

    try {
      const app = initializeApp(config);
      this.auth = getAuth(app);
      this.firebaseAuthReady = new Promise<void>((resolve) => {
        this.resolveFirebaseAuthReady = resolve;
      });

      const settleFirebaseAuthReady = (): void => {
        if (!this.firebaseRedirectHandled) {
          return;
        }
        if (this.resolveFirebaseAuthReady) {
          this.resolveFirebaseAuthReady();
          this.resolveFirebaseAuthReady = null;
        }
      };

      onAuthStateChanged(this.auth, (user) => {
        const previousUser = this.user;
        this.user = user;
        void this.syncAuthWithExtension(user, previousUser);
        this.notify();
        settleFirebaseAuthReady();
      });

      // Complete pending redirect sign-in before declaring auth settled. The first
      // `onAuthStateChanged` callback can fire with `null` while the credential is
      // still being applied; URL-prefix would otherwise redirect to `/join` and drop
      // a fresh mobile OAuth sign-in.
      try {
        await getRedirectResult(this.auth);
      } catch (error) {
        const code = extractFirebaseAuthErrorCode(error);
        if (code) {
          this.lastAuthErrorCode = code;
        }
        console.warn('[gist-try] getRedirectResult failed', error);
      } finally {
        this.firebaseRedirectHandled = true;
        this.syncConcreteUserFromAuth();
        settleFirebaseAuthReady();
      }

      /**
       * Do not eagerly create anonymous users on page load.
       *
       * Anonymous sign-in is performed lazily in getAuthorizationHeader() when a
       * request actually needs credentials (submit/speculation/authenticated APIs).
       * This avoids creating orphaned Firebase anonymous accounts for bounce traffic.
       */
      await this.firebaseAuthReady;
      this.syncConcreteUserFromAuth();
      if (this.user && !this.user.isAnonymous) {
        void this.ensureWebWorkerSessionCookieForFirebaseUser(this.user);
      }
      this.notify();
    } catch (error) {
      if (this.resolveFirebaseAuthReady) {
        this.resolveFirebaseAuthReady();
        this.resolveFirebaseAuthReady = null;
      }
      console.warn('[gist-try] Firebase init failed', error);
    }
  }

  /**
   * Clears the current extension worker token.
   *
   * Used by:
   * - try-client.ts when a worker token is rejected (401) and we want to fall back
   *   to a Firebase ID token without forcing a page refresh.
   *
   * @param reason - Best-effort reason for debugging/logging.
   */
  clearWorkerToken(reason: string = 'unknown'): void {
    if (!this.workerToken) {
      return;
    }
    this.workerToken = null;
    console.warn('[gist-try] Cleared extension worker token', { reason });
    this.notify();
  }

  clearWebSessionAuth(reason: string = 'unknown'): void {
    if (!this.webSessionUserKey && this.paidEntitlement === null) {
      return;
    }
    this.webSessionUserKey = null;
    this.paidEntitlement = null;
    console.warn('[gist-try] Cleared web session auth state', { reason });
    this.notify();
  }

  /**
   * Returns the active auth snapshot (extension > Firebase > none).
   */
  getSnapshot(): AuthSnapshot {
    const isConcreteFirebaseUser = Boolean(this.user && !this.user.isAnonymous);
    const isAuthenticated = Boolean(this.workerToken) || isConcreteFirebaseUser || Boolean(this.webSessionUserKey);

    if (this.workerToken) {
      return {
        mode: 'extension',
        workerToken: this.workerToken,
        user: this.user,
        isAuthenticated,
        paidEntitlement: this.paidEntitlement
      };
    }
    if (this.user) {
      return {
        mode: 'firebase',
        workerToken: null,
        user: this.user,
        isAuthenticated,
        paidEntitlement: this.paidEntitlement
      };
    }
    return {
      mode: 'none',
      workerToken: null,
      user: null,
      isAuthenticated,
      paidEntitlement: this.paidEntitlement
    };
  }

  /**
   * Subscribes to auth state changes.
   */
  subscribe(listener: (snapshot: AuthSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Returns an Authorization header string or null when unauthenticated.
   */
  async getAuthorizationHeader(options: AuthorizationHeaderOptions = {}): Promise<string | null> {
    const waitForExtensionMs = Math.max(0, options.waitForExtensionMs ?? 0);
    const allowAnonymous = options.allowAnonymous !== false;

    if (this.workerToken) {
      return `Bearer ${this.workerToken}`;
    }

    if (this.webSessionUserKey) {
      return null;
    }

    if (!this.hasConcreteFirebaseUser() && waitForExtensionMs > 0) {
      const extensionWait = await this.waitForExtensionAuthState(waitForExtensionMs);
      if (extensionWait === 'token_received' || extensionWait === 'token_present') {
        console.debug('[gist-url-prefix] extension worker token available after bridge wait', {
          outcome: extensionWait,
          timeoutMs: waitForExtensionMs
        });
      } else if (extensionWait === 'timeout') {
        console.debug('[gist-url-prefix] extension bridge wait expired without worker token', {
          timeoutMs: waitForExtensionMs
        });
      }
      if (this.workerToken) {
        return `Bearer ${this.workerToken}`;
      }
    }

    this.syncConcreteUserFromAuth();

    if (!this.user && allowAnonymous && this.auth && !this.autoSignInBlocked) {
      await this.ensureAnonymousSignIn();
      if (!this.user && this.auth.currentUser) {
        this.user = this.auth.currentUser;
      }
    }

    if (this.user && (allowAnonymous || !this.user.isAnonymous)) {
      try {
        const idToken = await this.user.getIdToken();
        this.lastAuthErrorCode = null;
        return `Bearer ${idToken}`;
      } catch (error) {
        console.warn('[gist-try] Failed to fetch Firebase ID token', error);
        return null;
      }
    }
    return null;
  }

  hasWebSessionAuth(): boolean {
    return Boolean(this.webSessionUserKey);
  }

  /**
   * Waits for the first extension bridge auth response.
   *
   * URL-prefix navigations are paid-member flows, so they need a brief chance
   * for the extension's worker token to arrive before falling back to web auth.
   */
  async waitForExtensionAuthState(
    timeoutMs: number
  ): Promise<'skipped' | 'token_present' | 'token_received' | 'timeout'> {
    if (this.workerToken) {
      return 'token_present';
    }
    if (timeoutMs <= 0) {
      return 'skipped';
    }
    let waiter: (() => void) | null = null;
    await Promise.race([
      new Promise<void>((resolve) => {
        waiter = resolve;
        this.extensionAuthStateWaiters.add(resolve);
      }),
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, timeoutMs);
      })
    ]);
    if (waiter) {
      this.extensionAuthStateWaiters.delete(waiter);
    }
    return this.workerToken ? 'token_received' : 'timeout';
  }

  /**
   * Returns the last Firebase auth error code (best effort).
   */
  getLastAuthErrorCode(): string | null {
    return this.lastAuthErrorCode;
  }

  /**
   * Signs out of Firebase and notifies the extension.
   */
  async logout(): Promise<void> {
    await this.clearWebWorkerSessionCookie();
    this.workerToken = null;
    this.webSessionUserKey = null;
    this.paidEntitlement = null;
    this.autoSignInBlocked = false;
    this.lastImportedUid = null;
    this.lastWebSessionMintUid = null;
    this.bridge.sendLogout();

    if (this.auth) {
      try {
        await signOut(this.auth);
      } catch (error) {
        console.warn('[gist-try] Firebase signOut failed', error);
      }
    }
    this.notify();
  }

  /**
   * Handles extension auth state updates.
   */
  private async handleExtensionAuthState(state: WebAuthState | null): Promise<void> {
    if (!state) {
      this.workerToken = null;
      // Extension explicitly logged out; keep web session usable but
      // suppress auto-import back into the extension.
      this.suppressExtensionImport = true;
      this.autoSignInBlocked = false;

      // Only sign out Firebase if the user is non-anonymous. Anonymous is kept
      // so /try remains functional even when the extension is logged out.
      if (this.auth && this.user && !this.user.isAnonymous) {
        try {
          await signOut(this.auth);
        } catch (error) {
          console.warn('[gist-try] Failed to sign out after extension logout', error);
        }
      }
      this.notify();
      return;
    }

    this.workerToken = state.workerToken;
    this.autoSignInBlocked = false;
    this.suppressExtensionImport = false;
    void this.ensureWebWorkerSessionCookie(`Bearer ${state.workerToken}`);
    this.resolveExtensionAuthStateWaiters();
    this.notify();
  }

  /**
   * Syncs Firebase auth changes into the extension session.
   */
  private async syncAuthWithExtension(nextUser: User | null, previousUser: User | null): Promise<void> {
    if (!this.bridge) {
      return;
    }

    if (!nextUser) {
      this.lastImportedUid = null;
    }

    if (nextUser) {
      this.autoSignInBlocked = false;
      if (!nextUser.isAnonymous) {
        void this.ensureWebWorkerSessionCookieForFirebaseUser(nextUser);
      }
      if (this.suppressExtensionImport) {
        return;
      }
      if (nextUser.isAnonymous) {
        return;
      }
      if (this.workerToken) {
        return;
      }
      const uid = nextUser.uid ?? null;
      if (uid && uid === this.lastImportedUid) {
        return;
      }

      try {
        const idToken = await nextUser.getIdToken();
        // Resolve across every provider so Google/Apple users don't land
        // in the extension with a null email on the first sync.
        const resolvedEmail = await resolveUserEmailWithReload(nextUser);
        this.bridge.sendAuthImport({
          idToken,
          email: resolvedEmail
        });
        this.lastImportedUid = uid;
      } catch (error) {
        console.warn('[gist-try] Failed to import auth into extension', error);
      }
      return;
    }

    if (previousUser && !this.autoSignInBlocked && !this.suppressExtensionImport) {
      this.bridge.sendLogout();
    }
  }

  /**
   * Ensures an anonymous Firebase session exists for ingest requests.
   */
  private async ensureAnonymousSignIn(): Promise<User | null> {
    if (!this.auth || this.anonymousSignInInFlight || this.autoSignInBlocked) {
      return this.user ?? this.auth?.currentUser ?? null;
    }

    this.anonymousSignInInFlight = true;
    try {
      // Anonymous sign-in is lazy (triggered only when credentials are needed),
      // which significantly reduces account churn vs eager page-load sign-ins.
      // A periodic cleanup job is still recommended on the Firebase project.
      const result = await signInAnonymously(this.auth);
      this.user = result.user ?? this.auth.currentUser ?? this.user;
      this.lastAuthErrorCode = null;
      this.notify();
      return this.user ?? null;
    } catch (error) {
      const code = extractFirebaseAuthErrorCode(error);
      this.lastAuthErrorCode = code;
      console.warn('[gist-try] Anonymous sign-in failed', error);
      return this.user ?? this.auth.currentUser ?? null;
    } finally {
      this.anonymousSignInInFlight = false;
    }
  }

  private hasConcreteFirebaseUser(): boolean {
    return Boolean(this.user && !this.user.isAnonymous);
  }

  /**
   * Mirrors a restored Firebase session into `this.user` when the SDK has already
   * hydrated `currentUser` but the first `onAuthStateChanged` callback has not run.
   */
  private async clearWebWorkerSessionCookie(): Promise<void> {
    const authorization = await this.getAuthorizationHeader({
      allowAnonymous: false,
      waitForExtensionMs: 0
    });
    await clearWebWorkerSession({
      authorization,
      clientVersion: 'web-try',
      logPrefix: '[gist-try]'
    });
  }

  private async ensureWebWorkerSessionCookie(authorization: string): Promise<void> {
    const body = await mintWebWorkerSession<TrySessionResponse>({
      authorization,
      clientVersion: 'web-try',
      logPrefix: '[gist-try]'
    });
    this.applySessionResponse(body);
  }

  private async ensureWebWorkerSessionCookieForFirebaseUser(user: User): Promise<void> {
    const uid = user.uid ?? null;
    if (!uid || uid === this.lastWebSessionMintUid || user.isAnonymous) {
      return;
    }
    try {
      const idToken = await user.getIdToken();
      const body = await mintWebWorkerSession<TrySessionResponse>({
        authorization: `Bearer ${idToken}`,
        clientVersion: 'web-try',
        logPrefix: '[gist-try]'
      });
      if (body) {
        this.lastWebSessionMintUid = uid;
      }
      this.applySessionResponse(body);
    } catch (error) {
      console.warn('[gist-try] Session mint failed', error);
    }
  }

  private applySessionResponse(body: TrySessionResponse | null): void {
    if (!body) {
      return;
    }
    this.webSessionUserKey = body.user?.uid ?? body.user?.email ?? this.webSessionUserKey;
    this.paidEntitlement = hasPaidEntitlement(body);
    this.notify();
  }

  private applyLandingBootstrap(): void {
    const landingUser = window.__GIST_LANDING_USER__ ?? null;
    if (!landingUser) {
      return;
    }
    this.webSessionUserKey = landingUser.uid;
    this.paidEntitlement = landingUser.paidEntitlement;
  }

  private syncConcreteUserFromAuth(): void {
    if (this.hasConcreteFirebaseUser()) {
      return;
    }
    const currentUser = this.auth?.currentUser ?? null;
    if (currentUser && !currentUser.isAnonymous) {
      this.user = currentUser;
    }
  }

  private resolveExtensionAuthStateWaiters(): void {
    if (!this.workerToken) {
      return;
    }
    for (const resolve of this.extensionAuthStateWaiters) {
      resolve();
    }
    this.extensionAuthStateWaiters.clear();
  }

  /**
   * Attempts a Google sign-in via Firebase popup.
   *
   * A-018: Mobile in-app browsers (Instagram, Twitter, etc.) block popups.
   * If signInWithPopup fails with auth/popup-blocked or auth/popup-closed-by-user,
   * we retry with signInWithRedirect as a fallback. The redirect flow completes
   * when the page reloads and Firebase's onAuthStateChanged fires automatically.
   */
  async signInWithGoogle(): Promise<User | null> {
    if (!this.auth) {
      throw new WebTryError('auth_not_configured');
    }

    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(this.auth, provider);
      this.user = result.user ?? this.auth.currentUser ?? this.user;
      this.autoSignInBlocked = false;
      this.suppressExtensionImport = false;
      this.notify();
      return this.user ?? null;
    } catch (error) {
      const code = extractFirebaseAuthErrorCode(error);

      // A-018: Popup blocked or closed — fall back to redirect flow.
      if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user') {
        console.warn('[gist-try] Popup blocked, falling back to redirect', { code });
        try {
          await signInWithRedirect(this.auth, provider);
          // signInWithRedirect navigates away; this line is only reached if
          // the browser somehow stays on the page (unlikely).
          return this.user ?? null;
        } catch (redirectError) {
          console.warn('[gist-try] Redirect sign-in also failed', redirectError);
          throw new WebTryError('google_sign_in_failed', { details: redirectError });
        }
      }

      console.warn('[gist-try] Google sign-in failed', error);
      throw new WebTryError('google_sign_in_failed', { details: error });
    }
  }

  /**
   * Attempts an email/password sign-in via Firebase.
   */
  async signInWithEmail(email: string, password: string): Promise<User | null> {
    if (!this.auth) {
      throw new WebTryError('auth_not_configured');
    }

    try {
      const result = await signInWithEmailAndPassword(this.auth, email, password);
      this.user = result.user ?? this.auth.currentUser ?? this.user;
      this.autoSignInBlocked = false;
      this.suppressExtensionImport = false;
      this.notify();
      return this.user ?? null;
    } catch (error) {
      const code = extractFirebaseAuthErrorCode(error);
      const message = code ?? 'email_sign_in_failed';
      console.warn('[gist-try] Email sign-in failed', { code, error });
      throw new WebTryError(message, { details: error });
    }
  }

  /**
   * Notifies listeners of auth state changes.
   */
  private notify(): void {
    if (this.listeners.size === 0) {
      return;
    }
    const snapshot = this.getSnapshot();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }
}
